import { z } from 'zod';
import { PaymentGatewayTerminalSaleStatus } from '../../../types/ipg/gateway-terminal-sale.types';
import {
    CloverLifecycleFinalizationGatewayOperation,
    CloverLifecycleFinalizationOutcome,
    CloverLifecycleFinalizationPayloadType,
    type CloverLifecycleFinalizationNormalizedResult,
    type CloverLifecycleFinalizationPayload,
    type CloverLifecycleFinalizationRequestDetails,
    type CloverLifecycleFinalizationRequestPayload,
    type CloverLifecycleFinalizationResponseDetails,
    type CloverLifecycleFinalizationResponsePayload,
} from '../../../types/ipg/clover/clover-lifecycle-finalization.types';
import { PaymentGatewayTerminalSaleResultSchema } from '../gateway-terminal-sale.schemas';
import { CloverTerminalSaleResponseDetailsSchema } from './clover-terminal-sale-response.schemas';

export const CloverLifecycleFinalizationTerminalSaleStatusSchema: z.ZodType<
    CloverLifecycleFinalizationNormalizedResult['status']
> = z.enum([
    PaymentGatewayTerminalSaleStatus.APPROVED,
    PaymentGatewayTerminalSaleStatus.DECLINED,
    PaymentGatewayTerminalSaleStatus.CANCELED,
    PaymentGatewayTerminalSaleStatus.MALFORMED_RESPONSE,
]);

export const CloverLifecycleFinalizationRequestDetailsSchema: z.ZodType<CloverLifecycleFinalizationRequestDetails> =
    z.object({
        gateway_operation: z.literal(CloverLifecycleFinalizationGatewayOperation.TERMINAL_SALE),
        terminal_sale_response_details: CloverTerminalSaleResponseDetailsSchema,
        terminal_sale_result: PaymentGatewayTerminalSaleResultSchema,
    });

export const CloverLifecycleFinalizationNormalizedResultSchema: z.ZodType<CloverLifecycleFinalizationNormalizedResult> =
    z.object({
        gateway_operation: z.literal(CloverLifecycleFinalizationGatewayOperation.TERMINAL_SALE),
        status: CloverLifecycleFinalizationTerminalSaleStatusSchema,
        gateway_transaction_id: z.string().min(1).optional(),
        approved_amount: z.string().min(1).optional(),
        authorization_code: z.string().min(1).optional(),
        gateway_token: z.string().min(1).optional(),
        message: z.string().min(1).optional(),
        response_code: z.string().min(1).optional(),
    });

export const CloverLifecycleFinalizationFinalizedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverLifecycleFinalizationOutcome.FINALIZED>;
    normalized_result: typeof CloverLifecycleFinalizationNormalizedResultSchema;
}> = z.object({
    outcome: z.literal(CloverLifecycleFinalizationOutcome.FINALIZED),
    normalized_result: CloverLifecycleFinalizationNormalizedResultSchema,
}) satisfies z.ZodType<
    Extract<
        CloverLifecycleFinalizationResponseDetails,
        {
            readonly outcome: typeof CloverLifecycleFinalizationOutcome.FINALIZED;
        }
    >
>;

export const CloverLifecycleFinalizationMalformedResultResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverLifecycleFinalizationOutcome.MALFORMED_RESULT>;
    message: z.ZodString;
    body: z.ZodUnknown;
}> = z.object({
    outcome: z.literal(CloverLifecycleFinalizationOutcome.MALFORMED_RESULT),
    message: z.string().min(1),
    body: z.unknown(),
}) satisfies z.ZodType<
    Extract<
        CloverLifecycleFinalizationResponseDetails,
        {
            readonly outcome: typeof CloverLifecycleFinalizationOutcome.MALFORMED_RESULT;
        }
    >
>;

export const CloverLifecycleFinalizationResponseDetailsSchema: z.ZodType<CloverLifecycleFinalizationResponseDetails> =
    z.discriminatedUnion('outcome', [
        CloverLifecycleFinalizationFinalizedResponseDetailsSchema,
        CloverLifecycleFinalizationMalformedResultResponseDetailsSchema,
    ]);

export const CloverLifecycleFinalizationRequestPayloadSchema: z.ZodObject<{
    gateway_lifecycle_payload_type: z.ZodLiteral<
        typeof CloverLifecycleFinalizationPayloadType.REQUEST
    >;
    request_details: typeof CloverLifecycleFinalizationRequestDetailsSchema;
}> = z.object({
    gateway_lifecycle_payload_type: z.literal(CloverLifecycleFinalizationPayloadType.REQUEST),
    request_details: CloverLifecycleFinalizationRequestDetailsSchema,
}) satisfies z.ZodType<CloverLifecycleFinalizationRequestPayload>;

export const CloverLifecycleFinalizationResponsePayloadSchema: z.ZodObject<{
    gateway_lifecycle_payload_type: z.ZodLiteral<
        typeof CloverLifecycleFinalizationPayloadType.RESPONSE
    >;
    response_details: typeof CloverLifecycleFinalizationResponseDetailsSchema;
}> = z.object({
    gateway_lifecycle_payload_type: z.literal(CloverLifecycleFinalizationPayloadType.RESPONSE),
    response_details: CloverLifecycleFinalizationResponseDetailsSchema,
}) satisfies z.ZodType<CloverLifecycleFinalizationResponsePayload>;

export const CloverLifecycleFinalizationPayloadSchema: z.ZodType<CloverLifecycleFinalizationPayload> =
    z.discriminatedUnion('gateway_lifecycle_payload_type', [
        CloverLifecycleFinalizationRequestPayloadSchema,
        CloverLifecycleFinalizationResponsePayloadSchema,
    ]);
